<template>
  <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg p-8 max-w-md w-full mx-4">
      <div class="text-center mb-6">
        <h2 class="text-2xl font-bold text-gray-900 mb-2">Change Password</h2>
        <p class="text-gray-600">Please change your password to continue</p>
      </div>

       <form @submit.prevent="handleChangePassword" class="space-y-6">
         <div>
          <label for="newPassword" class="block text-sm font-medium text-gray-700 mb-2">
            New Password
          </label>
          <input
            id="newPassword"
            v-model="form.newPassword"
            type="password"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter your new password"
            @input="validatePassword"
          />
          
          <div class="mt-2 text-sm">
            <div class="space-y-1">
              <div class="flex items-center" :class="passwordChecks.length ? 'text-green-600' : 'text-gray-500'">
                <span class="mr-2">{{ passwordChecks.length ? '✓' : '○' }}</span>
                At least 8 characters
              </div>
              <div class="flex items-center" :class="passwordChecks.lowercase ? 'text-green-600' : 'text-gray-500'">
                <span class="mr-2">{{ passwordChecks.lowercase ? '✓' : '○' }}</span>
                One lowercase letter
              </div>
              <div class="flex items-center" :class="passwordChecks.uppercase ? 'text-green-600' : 'text-gray-500'">
                <span class="mr-2">{{ passwordChecks.uppercase ? '✓' : '○' }}</span>
                One uppercase letter
              </div>
              <div class="flex items-center" :class="passwordChecks.special ? 'text-green-600' : 'text-gray-500'">
                <span class="mr-2">{{ passwordChecks.special ? '✓' : '○' }}</span>
                One special character
              </div>
            </div>
          </div>
        </div>

        <div>
          <label for="confirmPassword" class="block text-sm font-medium text-gray-700 mb-2">
            Confirm New Password
          </label>
          <input
            id="confirmPassword"
            v-model="form.confirmPassword"
            type="password"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Confirm your new password"
            @input="validatePassword"
          />
          <div v-if="form.confirmPassword && form.newPassword !== form.confirmPassword" class="text-blue-500 text-sm mt-1">
            Passwords do not match
          </div>
        </div>

        <div v-if="error" class="bg-blue-50 border border-blue-200 rounded-md p-3">
          <div class="text-blue-800 text-sm">
            {{ error }}
          </div>
        </div>

        <div v-if="success" class="bg-green-50 border border-green-200 rounded-md p-3">
          <div class="text-green-800 text-sm">
            {{ success }}
          </div>
        </div>

        <div class="flex space-x-4">
          
          <button
            type="submit"
            :disabled="!isFormValid || loading"
            class="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ loading ? 'Changing...' : 'Change Password' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, computed, reactive } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

export default {
  name: 'ChangePasswordModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    email: {
      type: String,
      required: true
    }
  },
  emits: ['close', 'success'],
  setup(props, { emit }) {
    const store = useStore()
    const router = useRouter()
    
     const form = reactive({
       newPassword: '',
       confirmPassword: ''
     })
    
    const passwordChecks = reactive({
      length: false,
      lowercase: false,
      uppercase: false,
      special: false
    })
    
    const error = ref('')
    const success = ref('')
    const loading = ref(false)
    
    const validatePassword = () => {
      const password = form.newPassword
      
      passwordChecks.length = password.length >= 8
      passwordChecks.lowercase = /[a-z]/.test(password)
      passwordChecks.uppercase = /[A-Z]/.test(password)
      passwordChecks.special = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    }
    
     const isFormValid = computed(() => {
       return form.newPassword && 
              form.confirmPassword &&
              form.newPassword === form.confirmPassword &&
              passwordChecks.length &&
              passwordChecks.lowercase &&
              passwordChecks.uppercase &&
              passwordChecks.special
     })
    
    const handleChangePassword = async () => {
      if (!isFormValid.value) return
      
      loading.value = true
      error.value = ''
      success.value = ''
      
       try {
         const requestData = {
           newPassword: form.newPassword,
           email: props.email
         };
         

         const url = process.env.VUE_APP_API_BASE_URL || '';
         const response = await fetch(`${url}/apinvoices/users/change-password-first-login`, {
           method: 'POST',
           headers: {
             'Content-Type': 'application/json',
           },
           credentials: 'include',
           body: JSON.stringify(requestData)
         })
        
        console.log("📡 Response status:", response.status);
        console.log("📡 Response headers:", Object.fromEntries(response.headers.entries()));
        
        // First get the response as text to see what we're actually receiving
        const responseText = await response.text();
        console.log("📡 Raw response text:", responseText);
        
        let data;
        try {
          data = JSON.parse(responseText);
          console.log("📡 Parsed JSON data:", data);
        } catch (parseError) {
          console.error("❌ JSON Parse Error:", parseError);
          console.error("❌ Raw response that failed to parse:", responseText);
          error.value = 'Server returned invalid response. Please try again.';
          return;
        }
        
         if (response.ok) {
           success.value = data.message || 'Password changed successfully!'
           
           // Update store with user data from response
           if (data.user) {
             console.log("👤 Updating store with user data:", data.user);
             store.commit('setUser', data.user);
           }
           
           // Clear form
           form.newPassword = ''
           form.confirmPassword = ''
          
          // Close modal and redirect after 1 second
          setTimeout(() => {
            emit('success')
            emit('close')
            // Redirect to home page
            router.push({ name: 'Home' });
          }, 1000)
        } else {
          if (data.details && Array.isArray(data.details)) {
            error.value = data.details.join(', ')
          } else {
            error.value = data.error || 'Failed to change password'
          }
        }
      } catch (err) {
        error.value = 'Network error. Please try again.'
        console.error('Change password error:', err)
      } finally {
        loading.value = false
      }
    }
    
     const handleCancel = () => {
       // Clear form
       form.newPassword = ''
       form.confirmPassword = ''
       error.value = ''
       success.value = ''
       emit('close')
     }
    
    return {
      form,
      passwordChecks,
      error,
      success,
      loading,
      isFormValid,
      validatePassword,
      handleChangePassword,
      handleCancel
    }
  }
}
</script>
